import { useState, useEffect } from 'react';
import axios from 'axios';
import { Calculator, TrendingUp, Calendar, CalendarDays, ChevronDown, ChevronRight, Download, AlertTriangle, Info } from 'lucide-react';
import { useEmpresa } from '@/context/EmpresaContext';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const formatPercent = (value) => {
  return `${(value || 0).toFixed(2)}%`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

// Calcular número de meses entre dos fechas
const mesesEntreFechas = (fechaInicio, fechaFin) => {
  const inicio = new Date(fechaInicio);
  const fin = new Date(fechaFin);
  return Math.round((fin - inicio) / (1000 * 60 * 60 * 24 * 30.44));
};

// Calcular cuotas ya pagadas desde fecha inicio hasta hoy
const cuotasPagadas = (fechaInicio) => {
  const inicio = new Date(fechaInicio);
  const hoy = new Date();
  const meses = Math.floor((hoy - inicio) / (1000 * 60 * 60 * 24 * 30.44));
  return Math.max(0, meses);
};

/**
 * MÉTODO FRANCÉS: Calcular tipo de interés anual a partir de los datos del préstamo
 * Usando el importe solicitado original, cuota mensual y plazo total
 * Fórmula: Cuota = Capital * (i * (1+i)^n) / ((1+i)^n - 1)
 * Se resuelve por Newton-Raphson
 */
const calcularTipoInteresMetodoFrances = (importeSolicitado, cuotaMensual, plazoTotalMeses) => {
  if (!importeSolicitado || importeSolicitado <= 0 || !cuotaMensual || cuotaMensual <= 0 || !plazoTotalMeses || plazoTotalMeses <= 0) {
    return 0;
  }

  // Si la cuota total es igual o menor al capital, no hay interés
  const totalPagado = cuotaMensual * plazoTotalMeses;
  if (totalPagado <= importeSolicitado) {
    return 0;
  }

  // Estimación inicial basada en el total de intereses
  const interesesTotales = totalPagado - importeSolicitado;
  let i = (interesesTotales / importeSolicitado) / (plazoTotalMeses / 12) / 12; // Tasa mensual inicial
  
  if (i <= 0) i = 0.005; // 6% anual como mínimo inicial
  
  // Newton-Raphson para encontrar la tasa exacta
  for (let iter = 0; iter < 100; iter++) {
    const factor = Math.pow(1 + i, plazoTotalMeses);
    const cuotaCalculada = importeSolicitado * (i * factor) / (factor - 1);
    
    const error = cuotaCalculada - cuotaMensual;
    if (Math.abs(error) < 0.01) {
      break;
    }
    
    // Derivada de la función de cuota respecto a i
    const h = 0.00001;
    const factor2 = Math.pow(1 + i + h, plazoTotalMeses);
    const cuotaCalculada2 = importeSolicitado * ((i + h) * factor2) / (factor2 - 1);
    const derivada = (cuotaCalculada2 - cuotaCalculada) / h;
    
    if (Math.abs(derivada) < 0.0001) break;
    
    i = i - error / derivada;
    
    // Límites razonables
    if (i < 0.0001) i = 0.0001; // Mínimo ~0.12% anual
    if (i > 0.05) i = 0.05; // Máximo ~60% anual
  }
  
  // Convertir tasa mensual a anual (en porcentaje)
  return i * 12 * 100;
};

/**
 * Generar cuadro de amortización MÉTODO FRANCÉS
 * Cuota fija mensual, amortización creciente, intereses decrecientes
 * @param capitalPendiente Capital pendiente actual
 * @param tipoInteresAnual Tipo de interés anual en %
 * @param cuotaMensualOriginal Cuota mensual original (para referencia)
 * @param mesesRestantes Número de meses restantes
 * @param fechaInicioProyeccion Fecha desde donde empezar la proyección (opcional)
 */
const generarCuadroAmortizacionFrances = (capitalPendiente, tipoInteresAnual, cuotaMensualOriginal, mesesRestantes, fechaInicioProyeccion = null) => {
  const cuotas = [];
  let capitalRestante = capitalPendiente;
  const tipoInteresMensual = (tipoInteresAnual / 100) / 12;
  
  // Fecha de inicio de las cuotas
  let fechaCuota;
  if (fechaInicioProyeccion) {
    fechaCuota = new Date(fechaInicioProyeccion);
  } else {
    // Por defecto: próximo mes desde hoy
    const hoy = new Date();
    fechaCuota = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 1);
  }
  
  // Calcular la cuota según método francés para el capital pendiente y cuotas restantes
  let cuotaFija;
  if (tipoInteresMensual > 0) {
    const factor = Math.pow(1 + tipoInteresMensual, mesesRestantes);
    cuotaFija = capitalPendiente * (tipoInteresMensual * factor) / (factor - 1);
  } else {
    cuotaFija = capitalPendiente / mesesRestantes;
  }
  
  let totalAmortizacion = 0;
  let totalInteres = 0;
  
  for (let i = 0; i < mesesRestantes; i++) {
    // Interés del mes sobre capital restante
    const interesMes = capitalRestante * tipoInteresMensual;
    
    // Amortización = Cuota - Interés
    let amortizacionMes = cuotaFija - interesMes;
    
    // Última cuota: ajustar para que el capital quede exactamente a 0
    if (i === mesesRestantes - 1) {
      amortizacionMes = capitalRestante;
    }
    
    const cuotaReal = amortizacionMes + interesMes;
    
    cuotas.push({
      numero: i + 1,
      fecha: new Date(fechaCuota),
      mes: fechaCuota.toLocaleDateString('es-ES', { month: 'short', year: 'numeric' }),
      mesCorto: fechaCuota.toLocaleDateString('es-ES', { month: 'short' }),
      año: fechaCuota.getFullYear(),
      mesNum: fechaCuota.getMonth() + 1,
      cuota: cuotaReal,
      amortizacion: amortizacionMes,
      interes: interesMes,
      capitalRestante: Math.max(0, capitalRestante - amortizacionMes)
    });
    
    totalAmortizacion += amortizacionMes;
    totalInteres += interesMes;
    capitalRestante -= amortizacionMes;
    fechaCuota.setMonth(fechaCuota.getMonth() + 1);
  }
  
  return { cuotas, totalAmortizacion, totalInteres };
};

// Agrupar cuotas por año
const agruparPorAño = (cuotas) => {
  const porAño = {};
  
  cuotas.forEach(cuota => {
    const año = cuota.año;
    if (!porAño[año]) {
      porAño[año] = {
        año,
        cuotas: [],
        totalCuota: 0,
        totalAmortizacion: 0,
        totalInteres: 0
      };
    }
    porAño[año].cuotas.push(cuota);
    porAño[año].totalCuota += cuota.cuota;
    porAño[año].totalAmortizacion += cuota.amortizacion;
    porAño[año].totalInteres += cuota.interes;
  });
  
  return Object.values(porAño).sort((a, b) => a.año - b.año);
};

// Agrupar cuotas por mes (nuevo)
const agruparPorMes = (cuotas) => {
  const porMes = {};
  
  cuotas.forEach(cuota => {
    const key = `${cuota.año}-${String(cuota.mesNum).padStart(2, '0')}`;
    if (!porMes[key]) {
      porMes[key] = {
        key,
        año: cuota.año,
        mes: cuota.mesNum,
        mesNombre: cuota.mes,
        totalCuota: 0,
        totalAmortizacion: 0,
        totalInteres: 0,
        detalles: []
      };
    }
    porMes[key].totalCuota += cuota.cuota;
    porMes[key].totalAmortizacion += cuota.amortizacion;
    porMes[key].totalInteres += cuota.interes;
  });
  
  return Object.values(porMes).sort((a, b) => a.key.localeCompare(b.key));
};

// Verificar si una operación tiene los campos obligatorios
const tienesCamposObligatorios = (op) => {
  return (
    op.importe_solicitado && op.importe_solicitado > 0 &&
    op.capital_pendiente && op.capital_pendiente > 0 &&
    op.cuota_mensual && op.cuota_mensual > 0 &&
    op.fecha_inicio &&
    op.fecha_fin
  );
};

export default function Proyecciones() {
  const { selectedEmpresa, API } = useEmpresa();
  const [operaciones, setOperaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOp, setSelectedOp] = useState('all');
  const [proyecciones, setProyecciones] = useState([]);
  const [operacionesIncompletas, setOperacionesIncompletas] = useState([]);
  const [expandedYears, setExpandedYears] = useState({});
  const [activeTab, setActiveTab] = useState('por-prestamo');
  const [modoAnioCompleto, setModoAnioCompleto] = useState(false); // Nuevo: modo año completo

  useEffect(() => {
    const fetchOperaciones = async () => {
      if (!selectedEmpresa) {
        setLoading(false);
        return;
      }
      try {
        const response = await axios.get(`${API}/operaciones-lp?empresa_id=${selectedEmpresa.id}`);
        const ops = response.data;
        setOperaciones(ops);
        
        // Separar operaciones completas e incompletas
        const completas = ops.filter(tienesCamposObligatorios);
        const incompletas = ops.filter(op => !tienesCamposObligatorios(op) && op.capital_pendiente > 0);
        
        setOperacionesIncompletas(incompletas);
        calcularProyecciones(completas);
      } catch (error) {
        console.error('Error fetching operaciones:', error);
        toast.error('Error al cargar las operaciones');
      } finally {
        setLoading(false);
      }
    };

    fetchOperaciones();
  }, [selectedEmpresa, API]);

  const calcularProyecciones = (ops, anioCompleto = false) => {
    const hoy = new Date();
    const proyeccionesCalc = [];
    const anioActual = hoy.getFullYear();

    ops.forEach(op => {
      if (!op.capital_pendiente || op.capital_pendiente <= 0) return;

      // Calcular plazo total y meses restantes
      const plazoTotalMeses = mesesEntreFechas(op.fecha_inicio, op.fecha_fin);
      const mesesPagados = cuotasPagadas(op.fecha_inicio);
      const mesesRestantes = Math.max(1, plazoTotalMeses - mesesPagados);
      
      // Determinar tipo de interés
      let tipoInteresAnual = op.tipo_interes || 0;
      let esEstimado = false;
      
      // Si no tenemos tipo de interés, lo calculamos con el método francés
      if (!tipoInteresAnual || tipoInteresAnual === 0) {
        tipoInteresAnual = calcularTipoInteresMetodoFrances(
          op.importe_solicitado,
          op.cuota_mensual,
          plazoTotalMeses
        );
        esEstimado = true;
      }

      // Calcular fecha de inicio de la proyección
      let fechaInicioProyeccion = null;
      let mesesAProyectar = mesesRestantes;
      
      if (anioCompleto) {
        // Modo año completo: mostrar las 12 cuotas del año actual
        // Esto es útil para el P&L anual
        fechaInicioProyeccion = new Date(anioActual, 0, 1); // 1 de enero
        const finAño = new Date(anioActual, 11, 31); // 31 de diciembre
        const fechaFin = new Date(op.fecha_fin);
        
        // Si el préstamo termina antes de fin de año, usar esa fecha
        const fechaLimite = fechaFin < finAño ? fechaFin : finAño;
        
        // Calcular meses desde enero hasta la fecha límite (máx 12)
        const mesesHastaLimite = Math.round((fechaLimite - fechaInicioProyeccion) / (1000 * 60 * 60 * 24 * 30.44)) + 1;
        mesesAProyectar = Math.min(Math.max(1, mesesHastaLimite), 12);
      }

      // Generar cuadro de amortización
      const { cuotas, totalAmortizacion, totalInteres } = generarCuadroAmortizacionFrances(
        op.capital_pendiente,
        tipoInteresAnual,
        op.cuota_mensual,
        mesesAProyectar,
        fechaInicioProyeccion
      );

      proyeccionesCalc.push({
        operacion: op,
        tipoInteresAnual,
        esEstimado,
        plazoTotalMeses,
        mesesPagados,
        mesesRestantes,
        mesesProyectados: mesesAProyectar,
        cuadro: cuotas,
        porAño: agruparPorAño(cuotas),
        totalAmortizacion,
        totalInteres,
        totalCuotas: totalAmortizacion + totalInteres,
        // Verificación: la amortización total debe ser igual al capital pendiente
        verificacion: Math.abs(totalAmortizacion - op.capital_pendiente) < 1
      });
    });

    setProyecciones(proyeccionesCalc);
  };

  // Recalcular cuando cambia el modo año completo
  useEffect(() => {
    const opsCompletas = operaciones.filter(tienesCamposObligatorios);
    if (opsCompletas.length > 0) {
      calcularProyecciones(opsCompletas, modoAnioCompleto);
    }
  }, [modoAnioCompleto]);

  const toggleYear = (opId, año) => {
    const key = `${opId}-${año}`;
    setExpandedYears(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const getProyeccionesFiltradas = () => {
    if (selectedOp === 'all') return proyecciones;
    return proyecciones.filter(p => p.operacion.id === selectedOp);
  };

  const getResumenAnual = () => {
    const proysFiltradas = getProyeccionesFiltradas();
    const resumenPorAño = {};

    proysFiltradas.forEach(proy => {
      proy.porAño.forEach(año => {
        if (!resumenPorAño[año.año]) {
          resumenPorAño[año.año] = {
            año: año.año,
            totalCuota: 0,
            totalAmortizacion: 0,
            totalInteres: 0,
            detalles: []
          };
        }
        resumenPorAño[año.año].totalCuota += año.totalCuota;
        resumenPorAño[año.año].totalAmortizacion += año.totalAmortizacion;
        resumenPorAño[año.año].totalInteres += año.totalInteres;
        resumenPorAño[año.año].detalles.push({
          entidad: proy.operacion.entidad_financiera,
          tipo: proy.operacion.tipologia_producto,
          cuota: año.totalCuota,
          amortizacion: año.totalAmortizacion,
          interes: año.totalInteres
        });
      });
    });

    return Object.values(resumenPorAño).sort((a, b) => a.año - b.año);
  };

  // Nuevo: Resumen mensual
  const getResumenMensual = () => {
    const proysFiltradas = getProyeccionesFiltradas();
    const resumenPorMes = {};

    proysFiltradas.forEach(proy => {
      proy.cuadro.forEach(cuota => {
        const key = `${cuota.año}-${String(cuota.mesNum).padStart(2, '0')}`;
        if (!resumenPorMes[key]) {
          resumenPorMes[key] = {
            key,
            año: cuota.año,
            mes: cuota.mesNum,
            mesNombre: cuota.mes,
            totalCuota: 0,
            totalAmortizacion: 0,
            totalInteres: 0,
            detalles: []
          };
        }
        resumenPorMes[key].totalCuota += cuota.cuota;
        resumenPorMes[key].totalAmortizacion += cuota.amortizacion;
        resumenPorMes[key].totalInteres += cuota.interes;
        resumenPorMes[key].detalles.push({
          entidad: proy.operacion.entidad_financiera,
          tipo: proy.operacion.tipologia_producto,
          cuota: cuota.cuota,
          amortizacion: cuota.amortizacion,
          interes: cuota.interes
        });
      });
    });

    return Object.values(resumenPorMes).sort((a, b) => a.key.localeCompare(b.key));
  };

  const exportarExcel = () => {
    const proysFiltradas = getProyeccionesFiltradas();
    const wb = XLSX.utils.book_new();

    // Helper para formatear moneda en Excel
    const formatMoney = (value) => {
      return new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value || 0);
    };

    // Calcular totales globales
    const totalGlobalCuotas = proysFiltradas.reduce((sum, p) => sum + p.totalCuotas, 0);
    const totalGlobalAmort = proysFiltradas.reduce((sum, p) => sum + p.totalAmortizacion, 0);
    const totalGlobalInt = proysFiltradas.reduce((sum, p) => sum + p.totalInteres, 0);

    // =====================
    // HOJA: RESUMEN ANUAL
    // =====================
    const resumenAnual = getResumenAnual();
    const dataResumen = [
      ['PROYECCIONES DE PRÉSTAMOS - RESUMEN ANUAL'],
      ['Empresa: ' + (selectedEmpresa?.nombre || '')],
      ['Fecha de exportación: ' + new Date().toLocaleDateString('es-ES')],
      ['Modo: ' + (modoAnioCompleto ? 'Año completo ' + new Date().getFullYear() : 'Cuotas pendientes')],
      [],
      ['Año', 'Total Cuotas', 'Amortización', 'Intereses'],
      ...resumenAnual.map(r => [
        r.año,
        formatMoney(r.totalCuota),
        formatMoney(r.totalAmortizacion),
        formatMoney(r.totalInteres)
      ]),
      [],
      ['TOTAL', formatMoney(totalGlobalCuotas), formatMoney(totalGlobalAmort), formatMoney(totalGlobalInt)]
    ];
    const wsResumen = XLSX.utils.aoa_to_sheet(dataResumen);
    // Ajustar anchos de columna
    wsResumen['!cols'] = [{ wch: 12 }, { wch: 18 }, { wch: 18 }, { wch: 18 }];
    XLSX.utils.book_append_sheet(wb, wsResumen, 'Resumen Anual');

    // =====================
    // HOJA: RESUMEN MENSUAL
    // =====================
    const resumenMensual = getResumenMensual();
    const dataMensual = [
      ['PROYECCIONES DE PRÉSTAMOS - RESUMEN MENSUAL'],
      ['Empresa: ' + (selectedEmpresa?.nombre || '')],
      ['Fecha de exportación: ' + new Date().toLocaleDateString('es-ES')],
      [],
      ['Mes', 'Total Cuotas', 'Amortización', 'Intereses'],
      ...resumenMensual.map(r => [
        r.mesNombre,
        formatMoney(r.totalCuota),
        formatMoney(r.totalAmortizacion),
        formatMoney(r.totalInteres)
      ]),
      [],
      ['TOTAL', formatMoney(totalGlobalCuotas), formatMoney(totalGlobalAmort), formatMoney(totalGlobalInt)]
    ];
    const wsMensual = XLSX.utils.aoa_to_sheet(dataMensual);
    wsMensual['!cols'] = [{ wch: 15 }, { wch: 18 }, { wch: 18 }, { wch: 18 }];
    XLSX.utils.book_append_sheet(wb, wsMensual, 'Resumen Mensual');

    // =====================
    // HOJAS: DETALLE POR PRÉSTAMO
    // =====================
    proysFiltradas.forEach((proy, idx) => {
      // Nombre de hoja sin caracteres especiales
      const entidadLimpia = proy.operacion.entidad_financiera
        .replace(/[^a-zA-Z0-9 ]/g, '')
        .substring(0, 15)
        .trim();
      const sheetName = `${entidadLimpia}_${idx + 1}`;
      
      const dataPrestamo = [
        ['CUADRO DE AMORTIZACIÓN'],
        [],
        ['Entidad:', proy.operacion.entidad_financiera],
        ['Tipo de producto:', proy.operacion.tipologia_producto],
        [],
        ['Importe Original:', formatMoney(proy.operacion.importe_solicitado)],
        ['Capital Pendiente:', formatMoney(proy.operacion.capital_pendiente)],
        ['Tipo de Interés:', proy.tipoInteresAnual.toFixed(2) + '%' + (proy.esEstimado ? ' (estimado)' : '')],
        ['Cuota Mensual:', formatMoney(proy.operacion.cuota_mensual)],
        [],
        ['Fecha Inicio:', formatDate(proy.operacion.fecha_inicio)],
        ['Fecha Fin:', formatDate(proy.operacion.fecha_fin)],
        ['Cuotas Restantes:', proy.mesesRestantes],
        [],
        [],
        ['Nº', 'Fecha', 'Cuota', 'Amortización', 'Interés', 'Capital Restante'],
        ...proy.cuadro.map(c => [
          c.numero,
          c.fecha.toLocaleDateString('es-ES'),
          formatMoney(c.cuota),
          formatMoney(c.amortizacion),
          formatMoney(c.interes),
          formatMoney(c.capitalRestante)
        ]),
        [],
        ['', 'TOTALES:', formatMoney(proy.totalCuotas), formatMoney(proy.totalAmortizacion), formatMoney(proy.totalInteres), '']
      ];
      const wsPrestamo = XLSX.utils.aoa_to_sheet(dataPrestamo);
      wsPrestamo['!cols'] = [
        { wch: 6 },   // Nº
        { wch: 12 },  // Fecha
        { wch: 16 },  // Cuota
        { wch: 16 },  // Amortización
        { wch: 16 },  // Interés
        { wch: 18 }   // Capital Restante
      ];
      XLSX.utils.book_append_sheet(wb, wsPrestamo, sheetName);
    });

    // Generar nombre de archivo con fecha
    const fechaExport = new Date().toISOString().split('T')[0];
    const nombreArchivo = `proyecciones_${selectedEmpresa?.nombre?.replace(/[^a-zA-Z0-9]/g, '_') || 'export'}_${fechaExport}.xlsx`;
    
    XLSX.writeFile(wb, nombreArchivo);
    toast.success('Proyecciones exportadas a Excel');
  };

  if (!selectedEmpresa) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center" data-testid="no-empresa-proyecciones">
        <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-4">
          <Calculator className="w-10 h-10 text-slate-400" />
        </div>
        <h2 className="text-2xl font-bold text-slate-800 font-['Outfit'] mb-2">
          Sin empresa seleccionada
        </h2>
        <p className="text-slate-500">
          Selecciona una empresa para ver las proyecciones de préstamos.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  const proysFiltradas = getProyeccionesFiltradas();
  const resumenAnual = getResumenAnual();
  const resumenMensual = getResumenMensual();
  const totalesGlobales = {
    amortizacion: proysFiltradas.reduce((sum, p) => sum + p.totalAmortizacion, 0),
    interes: proysFiltradas.reduce((sum, p) => sum + p.totalInteres, 0),
    cuotas: proysFiltradas.reduce((sum, p) => sum + p.totalCuotas, 0)
  };

  return (
    <TooltipProvider>
      <div className="space-y-6" data-testid="proyecciones-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-['Outfit']">
              Proyecciones de Préstamos
            </h1>
            <p className="text-slate-500 mt-1 flex items-center gap-2">
              Cuadro de amortización método francés - {selectedEmpresa.nombre}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-4 h-4 text-slate-400 cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-sm bg-slate-900 text-white p-3">
                  <p className="text-sm">
                    <strong>Método Francés:</strong> Cuota fija mensual con amortización creciente e intereses decrecientes.
                  </p>
                  <p className="text-sm mt-2">
                    <strong>Tipo de interés estimado:</strong> Cuando no se indica el tipo de interés, se calcula automáticamente usando el importe solicitado, la cuota mensual y el plazo del préstamo.
                  </p>
                </TooltipContent>
              </Tooltip>
            </p>
          </div>
          <div className="flex gap-3 items-center flex-wrap">
            {/* Switch para modo año completo */}
            <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2">
              <Switch
                id="modo-anio"
                checked={modoAnioCompleto}
                onCheckedChange={setModoAnioCompleto}
              />
              <Label htmlFor="modo-anio" className="text-sm cursor-pointer">
                {modoAnioCompleto ? (
                  <span className="text-amber-600 font-medium">Año completo {new Date().getFullYear()}</span>
                ) : (
                  <span className="text-slate-600">Cuotas pendientes</span>
                )}
              </Label>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-4 h-4 text-slate-400 cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs bg-slate-900 text-white p-2">
                  <p className="text-xs">
                    <strong>Cuotas pendientes:</strong> Desde hoy hasta fin del préstamo<br/>
                    <strong>Año completo:</strong> Las 12 cuotas del año actual (para P&L)
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
            
            <Select value={selectedOp} onValueChange={setSelectedOp}>
              <SelectTrigger 
                className="w-[280px] bg-white border-amber-300 focus:border-amber-500 focus:ring-amber-500" 
                data-testid="select-prestamo"
              >
                <SelectValue placeholder="Seleccionar préstamo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los préstamos</SelectItem>
                {proyecciones.map(p => (
                  <SelectItem key={p.operacion.id} value={p.operacion.id}>
                    {p.operacion.entidad_financiera} - {formatCurrency(p.operacion.capital_pendiente)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              onClick={exportarExcel} 
              className="bg-amber-500 hover:bg-amber-600 text-slate-900 flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Exportar
            </Button>
          </div>
        </div>

        {/* Info box explicativo */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">¿Cómo funcionan las proyecciones?</p>
              <ul className="space-y-1 text-blue-700">
                <li>• El <strong>tipo de interés</strong> se toma del préstamo si está disponible, o se <span className="text-amber-600 font-medium">(estima)</span> automáticamente a partir del importe, cuota y plazo.</li>
                <li>• La <strong>amortización total</strong> siempre coincide con el capital pendiente actual del préstamo.</li>
                <li>• El cuadro muestra las <strong>{modoAnioCompleto ? '12 cuotas del año ' + new Date().getFullYear() : 'cuotas futuras desde hoy'}</strong> hasta el vencimiento.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Aviso de operaciones incompletas */}
        {operacionesIncompletas.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">
                  {operacionesIncompletas.length} préstamo(s) sin datos completos
                </p>
                <p className="text-sm text-amber-700 mt-1">
                  Para calcular las proyecciones se necesitan: Importe solicitado, Capital pendiente, Cuota mensual, Fecha inicio y Fecha fin.
                </p>
                <ul className="text-sm text-amber-600 mt-2 space-y-1">
                  {operacionesIncompletas.map(op => (
                    <li key={op.id}>
                      • {op.entidad_financiera} - {op.tipologia_producto}: 
                      {!op.importe_solicitado && ' Falta importe solicitado,'}
                      {!op.cuota_mensual && ' Falta cuota mensual,'}
                      {!op.fecha_inicio && ' Falta fecha inicio,'}
                      {!op.fecha_fin && ' Falta fecha fin'}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Resumen Global */}
        {proysFiltradas.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm">
              <p className="text-sm text-slate-500 mb-1">Total Capital a Amortizar</p>
              <p className="text-2xl font-bold text-slate-900 font-mono-data">
                {formatCurrency(totalesGlobales.amortizacion)}
              </p>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm">
              <p className="text-sm text-slate-500 mb-1">Total Intereses a Pagar</p>
              <p className="text-2xl font-bold text-amber-600 font-mono-data">
                {formatCurrency(totalesGlobales.interes)}
              </p>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm">
              <p className="text-sm text-slate-500 mb-1">Total Cuotas Pendientes</p>
              <p className="text-2xl font-bold text-blue-600 font-mono-data">
                {formatCurrency(totalesGlobales.cuotas)}
              </p>
            </div>
          </div>
        )}

        {proysFiltradas.length === 0 ? (
          <div className="bg-white rounded-lg border border-slate-200 p-10 text-center">
            <Calculator className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No hay préstamos con datos completos para proyectar.</p>
            <p className="text-sm text-slate-400 mt-2">
              Asegúrate de completar: Importe solicitado, Capital pendiente, Cuota mensual, Fecha inicio y Fecha fin.
            </p>
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-amber-100 p-1">
              <TabsTrigger 
                value="por-prestamo" 
                className="flex items-center gap-2 data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900"
              >
                <TrendingUp className="w-4 h-4" />
                Por Préstamo
              </TabsTrigger>
              <TabsTrigger 
                value="por-mes" 
                className="flex items-center gap-2 data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900"
              >
                <CalendarDays className="w-4 h-4" />
                Resumen Mensual
              </TabsTrigger>
              <TabsTrigger 
                value="por-año" 
                className="flex items-center gap-2 data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900"
              >
                <Calendar className="w-4 h-4" />
                Resumen Anual
              </TabsTrigger>
            </TabsList>

            {/* Tab: Por Préstamo */}
            <TabsContent value="por-prestamo" className="space-y-6">
              {proysFiltradas.map((proy) => (
                <div key={proy.operacion.id} className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
                  {/* Header del préstamo */}
                  <div className="bg-slate-50 p-4 border-b border-slate-200">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div>
                        <h3 className="text-lg font-semibold text-slate-800">
                          {proy.operacion.entidad_financiera} - {proy.operacion.tipologia_producto}
                        </h3>
                        <div className="text-sm text-slate-500 mt-1 space-y-0.5">
                          <p>
                            Importe original: <span className="font-mono-data font-medium">{formatCurrency(proy.operacion.importe_solicitado)}</span>
                            {' · '}
                            Capital pendiente: <span className="font-mono-data font-medium">{formatCurrency(proy.operacion.capital_pendiente)}</span>
                          </p>
                          <p>
                            Tipo interés: <span className="font-mono-data font-medium">
                              {formatPercent(proy.tipoInteresAnual)}
                              {proy.esEstimado && <span className="text-amber-600 ml-1">(estimado)</span>}
                            </span>
                            {' · '}
                            Cuota mensual: <span className="font-mono-data font-medium">{formatCurrency(proy.operacion.cuota_mensual)}</span>
                            {' · '}
                            {proy.mesesRestantes} cuotas restantes de {proy.plazoTotalMeses}
                          </p>
                          <p>
                            <span className="text-slate-600">Fecha inicio:</span> <span className="font-mono-data font-medium">{formatDate(proy.operacion.fecha_inicio)}</span>
                            {' · '}
                            <span className="text-slate-600">Fecha fin:</span> <span className="font-mono-data font-medium">{formatDate(proy.operacion.fecha_fin)}</span>
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-6 text-sm">
                        <div className="text-center">
                          <p className="text-slate-500">Amortización</p>
                          <p className="font-mono-data font-semibold text-slate-800">{formatCurrency(proy.totalAmortizacion)}</p>
                          {!proy.verificacion && (
                            <p className="text-xs text-red-500">⚠ No cuadra</p>
                          )}
                        </div>
                        <div className="text-center">
                          <p className="text-slate-500">Intereses</p>
                          <p className="font-mono-data font-semibold text-amber-600">{formatCurrency(proy.totalInteres)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-slate-500">Total a pagar</p>
                          <p className="font-mono-data font-semibold text-blue-600">{formatCurrency(proy.totalCuotas)}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Cuadro por años (colapsable) */}
                  <div className="divide-y divide-slate-100">
                    {proy.porAño.map((año) => (
                      <Collapsible
                        key={año.año}
                        open={expandedYears[`${proy.operacion.id}-${año.año}`]}
                        onOpenChange={() => toggleYear(proy.operacion.id, año.año)}
                      >
                        <CollapsibleTrigger className="w-full">
                          <div className="flex items-center justify-between p-4 hover:bg-slate-50 cursor-pointer">
                            <div className="flex items-center gap-3">
                              {expandedYears[`${proy.operacion.id}-${año.año}`] ? (
                                <ChevronDown className="w-5 h-5 text-slate-400" />
                              ) : (
                                <ChevronRight className="w-5 h-5 text-slate-400" />
                              )}
                              <span className="font-semibold text-slate-800">{año.año}</span>
                              <span className="text-sm text-slate-500">({año.cuotas.length} cuotas)</span>
                            </div>
                            <div className="flex gap-6 text-sm">
                              <div>
                                <span className="text-slate-500">Cuotas: </span>
                                <span className="font-mono-data font-medium">{formatCurrency(año.totalCuota)}</span>
                              </div>
                              <div>
                                <span className="text-slate-500">Amort: </span>
                                <span className="font-mono-data font-medium">{formatCurrency(año.totalAmortizacion)}</span>
                              </div>
                              <div>
                                <span className="text-slate-500">Int: </span>
                                <span className="font-mono-data font-medium text-amber-600">{formatCurrency(año.totalInteres)}</span>
                              </div>
                            </div>
                          </div>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="px-4 pb-4">
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-slate-50">
                                  <TableHead className="text-xs">Nº</TableHead>
                                  <TableHead className="text-xs">Mes</TableHead>
                                  <TableHead className="text-xs text-right">Cuota</TableHead>
                                  <TableHead className="text-xs text-right">Amortización</TableHead>
                                  <TableHead className="text-xs text-right">Interés</TableHead>
                                  <TableHead className="text-xs text-right">Capital Rest.</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {año.cuotas.map((cuota) => (
                                  <TableRow key={cuota.numero} className="text-sm">
                                    <TableCell className="py-2">{cuota.numero}</TableCell>
                                    <TableCell className="py-2">{cuota.mes}</TableCell>
                                    <TableCell className="py-2 text-right font-mono-data">{formatCurrency(cuota.cuota)}</TableCell>
                                    <TableCell className="py-2 text-right font-mono-data">{formatCurrency(cuota.amortizacion)}</TableCell>
                                    <TableCell className="py-2 text-right font-mono-data text-amber-600">{formatCurrency(cuota.interes)}</TableCell>
                                    <TableCell className="py-2 text-right font-mono-data">{formatCurrency(cuota.capitalRestante)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    ))}
                  </div>
                </div>
              ))}
            </TabsContent>

            {/* Tab: Resumen Mensual (NUEVO) */}
            <TabsContent value="por-mes" className="space-y-6">
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
                <div className="max-h-[600px] overflow-y-auto">
                  <Table>
                    <TableHeader className="sticky top-0 bg-white z-10">
                      <TableRow className="bg-slate-50">
                        <TableHead className="font-medium">Mes</TableHead>
                        <TableHead className="font-medium text-right">Total Cuotas</TableHead>
                        <TableHead className="font-medium text-right">Amortización</TableHead>
                        <TableHead className="font-medium text-right">Intereses</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {resumenMensual.map((mes) => (
                        <TableRow key={mes.key}>
                          <TableCell className="font-medium">{mes.mesNombre}</TableCell>
                          <TableCell className="text-right font-mono-data">{formatCurrency(mes.totalCuota)}</TableCell>
                          <TableCell className="text-right font-mono-data">{formatCurrency(mes.totalAmortizacion)}</TableCell>
                          <TableCell className="text-right font-mono-data text-amber-600">{formatCurrency(mes.totalInteres)}</TableCell>
                        </TableRow>
                      ))}
                      {/* Fila de totales */}
                      <TableRow className="bg-slate-50 font-semibold sticky bottom-0">
                        <TableCell>TOTAL</TableCell>
                        <TableCell className="text-right font-mono-data">{formatCurrency(totalesGlobales.cuotas)}</TableCell>
                        <TableCell className="text-right font-mono-data">{formatCurrency(totalesGlobales.amortizacion)}</TableCell>
                        <TableCell className="text-right font-mono-data text-amber-600">{formatCurrency(totalesGlobales.interes)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TabsContent>

            {/* Tab: Resumen Anual */}
            <TabsContent value="por-año" className="space-y-6">
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-medium">Año</TableHead>
                      <TableHead className="font-medium text-right">Total Cuotas</TableHead>
                      <TableHead className="font-medium text-right">Amortización</TableHead>
                      <TableHead className="font-medium text-right">Intereses</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {resumenAnual.map((año) => (
                      <TableRow key={año.año}>
                        <TableCell className="font-semibold">{año.año}</TableCell>
                        <TableCell className="text-right font-mono-data">{formatCurrency(año.totalCuota)}</TableCell>
                        <TableCell className="text-right font-mono-data">{formatCurrency(año.totalAmortizacion)}</TableCell>
                        <TableCell className="text-right font-mono-data text-amber-600">{formatCurrency(año.totalInteres)}</TableCell>
                      </TableRow>
                    ))}
                    {/* Fila de totales */}
                    <TableRow className="bg-slate-50 font-semibold">
                      <TableCell>TOTAL</TableCell>
                      <TableCell className="text-right font-mono-data">{formatCurrency(totalesGlobales.cuotas)}</TableCell>
                      <TableCell className="text-right font-mono-data">{formatCurrency(totalesGlobales.amortizacion)}</TableCell>
                      <TableCell className="text-right font-mono-data text-amber-600">{formatCurrency(totalesGlobales.interes)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              {/* Detalle por préstamo en cada año */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-800">Detalle por Año</h3>
                {resumenAnual.map((año) => (
                  <Collapsible
                    key={año.año}
                    open={expandedYears[`resumen-${año.año}`]}
                    onOpenChange={() => setExpandedYears(prev => ({
                      ...prev,
                      [`resumen-${año.año}`]: !prev[`resumen-${año.año}`]
                    }))}
                  >
                    <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
                      <CollapsibleTrigger className="w-full">
                        <div className="flex items-center justify-between p-4 hover:bg-slate-50 cursor-pointer">
                          <div className="flex items-center gap-3">
                            {expandedYears[`resumen-${año.año}`] ? (
                              <ChevronDown className="w-5 h-5 text-slate-400" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-slate-400" />
                            )}
                            <span className="text-lg font-semibold text-slate-800">{año.año}</span>
                          </div>
                          <div className="flex gap-6">
                            <span className="font-mono-data font-semibold">{formatCurrency(año.totalCuota)}</span>
                          </div>
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="border-t border-slate-100">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-slate-50">
                                <TableHead className="text-xs">Entidad</TableHead>
                                <TableHead className="text-xs">Tipo</TableHead>
                                <TableHead className="text-xs text-right">Cuotas</TableHead>
                                <TableHead className="text-xs text-right">Amortización</TableHead>
                                <TableHead className="text-xs text-right">Intereses</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {año.detalles.map((det, idx) => (
                                <TableRow key={idx} className="text-sm">
                                  <TableCell className="py-2">{det.entidad}</TableCell>
                                  <TableCell className="py-2">{det.tipo}</TableCell>
                                  <TableCell className="py-2 text-right font-mono-data">{formatCurrency(det.cuota)}</TableCell>
                                  <TableCell className="py-2 text-right font-mono-data">{formatCurrency(det.amortizacion)}</TableCell>
                                  <TableCell className="py-2 text-right font-mono-data text-amber-600">{formatCurrency(det.interes)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </TooltipProvider>
  );
}
